// stores/mainStore.js
import { defineStore } from "pinia";
import {
  getWebsiteHomePageInfo,
  getMallGoodsMenuList,
  getWebStorePageInfo,
  getActiveDetail,
} from "~/api";
import {
  getCurrentUserData,
  getCurrentMallUserData,
  getCurrencyDataList,
  queryDefaultCurrencyCode,
  queryCurrencyRate,
  getCartList
} from "~/api/mall";
import { resolveSiteTemplate } from "@/templates/_core/getCurrentTemplate";

import { get, set } from "lodash-es";

import {
  mockJwtMap,
  getPrefixUrl,
  getCurrentServer,
  getOpenAccessTokenKey,
} from "@/utils/globalData";

import config from "@/config/index";
import { traceSsr } from "@/utils/ssrTrace";
import { normalizeMallGoodsMenuList } from "@/utils/mallGoodsMenu";
import {
  getCurrentCurrencyStorageKey,
  readStoredCurrentCurrency,
} from "@/utils/displayCurrency";
// 创建 Pinia store

const STORE_UNSAFE_KEYS = new Set([
  "hasOwnProperty",
  "__proto__",
  "constructor",
  "prototype",
]);

function toPlainSerializable(value, seen = new WeakMap()) {
  if (Array.isArray(value)) {
    return value.map((item) => toPlainSerializable(item, seen));
  }

  if (value && typeof value === "object") {
    if (seen.has(value)) {
      return seen.get(value);
    }

    const result = {};
    seen.set(value, result);

    return Object.keys(value).reduce((result, key) => {
      if (STORE_UNSAFE_KEYS.has(key)) {
        return result;
      }

      result[key] = toPlainSerializable(value[key], seen);
      return result;
    }, result);
  }

  return value;
}

function normalizeConfigObject(configData) {
  if (!configData) {
    return {};
  }

  if (typeof configData === "string") {
    try {
      return JSON.parse(configData) || {};
    } catch (e) {
      return {};
    }
  }

  if (typeof configData === "object") {
    return configData;
  }

  return {};
}

function getSiteConfigData(siteInfo = {}) {
  return normalizeConfigObject(get(siteInfo, "configData"));
}

function createDevelopmentFallbackSiteInfo(currentServer = "") {
  const devShopStyle = process.env.NUXT_PUBLIC_DEV_SHOP_STYLE || "";
  const normalizedCurrentServer = String(currentServer || "").trim();

  return {
    id: "development-site-fallback",
    title: "星际货运(中转)",
    pc_SITE_NAME: "星际货运(中转)",
    mobile_SITE_NAME: "星际货运(中转)",
    siteCode: "development",
    defaultLocale: config.defaultLocale,
    shopStyle: devShopStyle,
    configData: {
      shopStyle: devShopStyle,
      domainTree: normalizedCurrentServer
        ? [{ domain: normalizedCurrentServer, children: [] }]
        : [],
    },
  };
}

function shouldUseDevelopmentSiteInfoFallback() {
  return process.env.NODE_ENV === "development"
    && (
      process.env.DEV_SITE_INFO_FALLBACK === "1"
      || process.env.NUXT_PUBLIC_DEV_SITE_INFO_FALLBACK === "1"
    );
}

function withDevelopmentTimeout(promise, timeoutMs = 8000) {
  if (process.env.NODE_ENV !== "development") {
    return promise;
  }

  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Development request timeout after ${timeoutMs}ms`));
      }, timeoutMs);
    }),
  ]);
}

export const useMainStore = defineStore("main", {
  state: () => ({
    token: "",
    userInfo: {},
    mallUserInfo: {},
    tokenKeyMap: {},
    shopIdKeyMap: {},
    dealerShopIdKeyMap: {},
    shopDefaultCurrencyCodeMap: {},
    storeInfoDataMap: {},
    currencyList: [],
    currentCurrency: {},
    layoutData: {},
    menuData: [],
    menuIdMap: {},
    navigationList: [],
    siteInfo: {},
    domainName: "", // 域名信息
    platformSource: "", // 平台来源
  }),

  actions: {
    // 设置 Token
    setToken(token) {
      this.token = token;
    },

    // 设置 allGoodsListMap
    setAllGoodsListMap(allGoodsListMap) {
      this.allGoodsListMap = toPlainSerializable(allGoodsListMap || {});
    },

    // 获取 allGoodsListMap
    getAllGoodsListMap() {
      return this.allGoodsListMap;
    },

    // 设置用户信息
    setUserInfo(userInfo) {
      this.userInfo = toPlainSerializable(userInfo || {});
    },
    getUserInfo() {
      return this.userInfo;
    },

    // 设置菜单数据
    setMenuData(menuData) {
      this.menuData = toPlainSerializable(menuData || []);
    },

    // 设置菜单 ID Map
    setMenuIdMap(menuIdMap) {
      this.menuIdMap = toPlainSerializable(menuIdMap || {});
    },
    // 设置 不同店铺的map
    setStoreInfoDataMap(key, val) {
      this.storeInfoDataMap[key] = toPlainSerializable(val);
    },
    // 获取 不同店铺的值
    getStoreInfoDataMap(key) {
      return this.storeInfoDataMap[key];
    },
    // 设置 Token Key Map
    setTokenKeyMap(key, val) {
      this.tokenKeyMap[key] = toPlainSerializable(val);
    },
    setShopIdKeyMap(key, val) {
      this.shopIdKeyMap[key] = toPlainSerializable(val);
    },
    setDealerShopIdKeyMap(key, val) {
      this.dealerShopIdKeyMap[key] = toPlainSerializable(val);
    },
    setShopDefaultCurrencyCode(dealerShopId, currencyCode) {
      const normalizedDealerShopId = String(dealerShopId || "").trim();
      if (!normalizedDealerShopId) {
        return;
      }

      const normalizedCurrencyCode = String(currencyCode || "")
        .trim()
        .toUpperCase();

      this.shopDefaultCurrencyCodeMap[normalizedDealerShopId] =
        toPlainSerializable(normalizedCurrencyCode);
    },
    getShopDefaultCurrencyCode(
      dealerShopId = "",
      currentServer = getCurrentServer()
    ) {
      const resolvedDealerShopId = String(
        dealerShopId || this.getServerShopId(currentServer)
      ).trim();

      if (!resolvedDealerShopId) {
        return "";
      }

      return String(
        this.shopDefaultCurrencyCodeMap[resolvedDealerShopId] || ""
      )
        .trim()
        .toUpperCase();
    },

    // 设置站点信息
    setSiteInfo(data) {
      this.siteInfo = toPlainSerializable(data || {});
    },
    getSiteInfo() {
      return this.siteInfo;
    },

    // 设置域名信息
    setDomainName(domainName) {
      this.domainName = domainName;
      // 同时存储到 localStorage
      if (process.client) {
        sessionStorage.setItem('domainName', domainName);
      }
    },
    getDomainName() {
      return this.domainName;
    },

    // 设置平台来源
    setPlatformSource(platformSource) {
      this.platformSource = platformSource;
      // 同时存储到 localStorage
      if (process.client) {
        sessionStorage.setItem('platformSource', platformSource);
      }
    },
    getPlatformSource() {
      return this.platformSource;
    },
    getMallSiteConfig(currentServer = getCurrentServer()) {
      const storeInfo = this.storeInfoDataMap[currentServer] || {};
      return (
        get(storeInfo, "storePageInfo.mallSiteConfig") ||
        get(storeInfo, "storePageInfo.otherJson.mallSiteConfig") ||
        get(storeInfo, "mallSiteConfig") ||
        {}
      );
    },
    getMallSiteDefaultCurrency(currentServer = getCurrentServer()) {
      return this.getShopDefaultCurrencyCode("", currentServer);
    },
    getDealerShopId(currentServer = getCurrentServer()) {
      const storeInfo = this.storeInfoDataMap[currentServer] || {};
      const siteConfigData = getSiteConfigData(this.siteInfo);
      const candidateList = [
        get(siteConfigData, "dealerShopId"),
        get(this.siteInfo, "dealerShopId"),
        get(storeInfo, "dealerShopId"),
        get(storeInfo, "storePageInfo.dealerShopId"),
        get(storeInfo, "storePageInfo.otherJson.dealerShopId"),
        get(storeInfo, "storePageInfo.mallSiteConfig.dealerShopId"),
        get(storeInfo, "storePageInfo.otherJson.mallSiteConfig.dealerShopId"),
        // Legacy fallback only: storeInfo.id can be the store-config record id.
        get(storeInfo, "id"),
      ];

      const matchedDealerShopId = candidateList.find((item) => {
        return String(item || "").trim();
      });

      traceSsr("dealerShopId.resolve", {
        currentServer,
        candidates: candidateList.map((item) => String(item || "").trim()),
        resolvedDealerShopId: String(matchedDealerShopId || "").trim(),
      });

      return String(matchedDealerShopId || "").trim();
    },
    getShopId(currentServer = getCurrentServer()) {
      const tokenKey = getOpenAccessTokenKey(currentServer);
      const storeInfo = this.storeInfoDataMap[currentServer] || {};
      const siteConfigData = getSiteConfigData(this.siteInfo);
      const candidateList = [
        this.shopIdKeyMap[tokenKey],
        get(siteConfigData, "shopId"),
        get(this.siteInfo, "shopId"),
        get(storeInfo, "shopId"),
        get(storeInfo, "storePageInfo.shopId"),
        get(storeInfo, "storePageInfo.otherJson.shopId"),
        get(storeInfo, "storePageInfo.mallSiteConfig.shopId"),
        get(storeInfo, "storePageInfo.otherJson.mallSiteConfig.shopId"),
      ];
      const matchedShopId = candidateList.find((item) => {
        return String(item || "").trim();
      });

      traceSsr("shopId.resolve", {
        currentServer,
        tokenKey,
        candidates: candidateList.map((item) => String(item || "").trim()),
        resolvedShopId: String(matchedShopId || "").trim(),
      });

      return String(matchedShopId || "").trim();
    },
    getServerShopId(currentServer = getCurrentServer()) {
      const tokenKey = getOpenAccessTokenKey(currentServer);
      const shopId = this.dealerShopIdKeyMap[tokenKey] || "";
      traceSsr("serverShopId.resolve", {
        currentServer,
        tokenKey,
        mapValue: String(this.dealerShopIdKeyMap[tokenKey] || "").trim(),
        resolvedServerShopId: String(shopId || "").trim(),
      });
      return shopId;
    },
    buildCurrencyList(currencyList = [], baseCurrencyCode = "") {
      const fallbackBaseCurrencyCode =
        this.getMallSiteDefaultCurrency(getCurrentServer()) || "TWD";
      const normalizedBaseCurrencyCode = String(
        baseCurrencyCode || fallbackBaseCurrencyCode
      )
        .trim()
        .toUpperCase() || fallbackBaseCurrencyCode;
      const nextCurrencyList = [
        {
          id: "1",
          fromCurrencyCode: normalizedBaseCurrencyCode,
          toCurrencyCode: normalizedBaseCurrencyCode,
          exchangeRate: 1,
          activeFlag: true,
          deleteFlag: false,
        },
        ...currencyList,
      ];
      const seenCurrencyMap = new Set();

      return nextCurrencyList
        .map((item) => {
          const fromCurrencyCode = String(
            item?.fromCurrencyCode || normalizedBaseCurrencyCode
          ).trim().toUpperCase();
          const toCurrencyCode = String(item?.toCurrencyCode || "")
            .trim()
            .toUpperCase();
          const exchangeRate = Number(item?.exchangeRate || 0) || 0;

          return {
            ...item,
            fromCurrencyCode,
            toCurrencyCode,
            exchangeRate,
          };
        })
        .filter((item) => {
          if (!item.toCurrencyCode) {
            return false;
          }

          const currencyKey = item.toCurrencyCode;
          if (seenCurrencyMap.has(currencyKey)) {
            return false;
          }

          seenCurrencyMap.add(currencyKey);
          return true;
        });
    },
    normalizeCurrencyRateList(currencyList = []) {
      if (!Array.isArray(currencyList)) {
        return [];
      }

      return currencyList
        .map((item) => {
          const fromCurrencyCode = String(
            item?.fromCurrencyCode || ""
          ).trim().toUpperCase();
          const toCurrencyCode = String(item?.toCurrencyCode || "")
            .trim()
            .toUpperCase();
          const exchangeRate = Number(item?.exchangeRate || 0);

          return {
            ...item,
            fromCurrencyCode,
            toCurrencyCode,
            exchangeRate,
          };
        })
        .filter((item) => {
          return (
            item.fromCurrencyCode &&
            item.toCurrencyCode &&
            Number.isFinite(item.exchangeRate) &&
            item.exchangeRate > 0
          );
        });
    },
    buildCurrencyRateGraph(currencyList = []) {
      const graph = new Map();
      const addEdge = (fromCurrencyCode, toCurrencyCode, exchangeRate) => {
        if (!fromCurrencyCode || !toCurrencyCode || !exchangeRate) {
          return;
        }

        if (!graph.has(fromCurrencyCode)) {
          graph.set(fromCurrencyCode, new Map());
        }

        graph.get(fromCurrencyCode).set(toCurrencyCode, exchangeRate);
      };

      this.normalizeCurrencyRateList(currencyList).forEach((item) => {
        const exchangeRate = Number(item.exchangeRate);
        if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
          return;
        }

        addEdge(item.fromCurrencyCode, item.toCurrencyCode, exchangeRate);
        addEdge(item.toCurrencyCode, item.fromCurrencyCode, 1 / exchangeRate);
      });

      return graph;
    },
    resolveCurrencyExchangeRate(graph, fromCurrencyCode, toCurrencyCode) {
      const fromCode = String(fromCurrencyCode || "").trim().toUpperCase();
      const toCode = String(toCurrencyCode || "").trim().toUpperCase();

      if (!fromCode || !toCode) {
        return 0;
      }

      if (fromCode === toCode) {
        return 1;
      }

      const visitedCurrencySet = new Set([fromCode]);
      const queue = [[fromCode, 1]];

      while (queue.length) {
        const [currentCurrencyCode, currentExchangeRate] = queue.shift();
        const nextCurrencyMap = graph.get(currentCurrencyCode);

        if (!nextCurrencyMap) {
          continue;
        }

        for (const [
          nextCurrencyCode,
          nextExchangeRate,
        ] of nextCurrencyMap.entries()) {
          if (visitedCurrencySet.has(nextCurrencyCode)) {
            continue;
          }

          const resolvedExchangeRate =
            Number(currentExchangeRate) * Number(nextExchangeRate);

          if (nextCurrencyCode === toCode) {
            return resolvedExchangeRate;
          }

          visitedCurrencySet.add(nextCurrencyCode);
          queue.push([nextCurrencyCode, resolvedExchangeRate]);
        }
      }

      return 0;
    },
    buildDisplayCurrencyList(currencyList = [], defaultCurrencyCode = "") {
      const normalizedCurrencyList = this.normalizeCurrencyRateList(currencyList);
      const currencyRateGraph =
        this.buildCurrencyRateGraph(normalizedCurrencyList);
      const fallbackDefaultCurrencyCode =
        this.getMallSiteDefaultCurrency(getCurrentServer()) || "TWD";
      const normalizedDefaultCurrencyCode = String(
        defaultCurrencyCode || fallbackDefaultCurrencyCode
      )
        .trim()
        .toUpperCase() || fallbackDefaultCurrencyCode;
      const baseCurrencyCode = normalizedDefaultCurrencyCode;
      const currencyCodeSet = new Set([baseCurrencyCode]);

      normalizedCurrencyList.forEach((item) => {
        if (item.fromCurrencyCode) {
          currencyCodeSet.add(item.fromCurrencyCode);
        }

        if (item.toCurrencyCode) {
          currencyCodeSet.add(item.toCurrencyCode);
        }
      });

      const displayCurrencyList = Array.from(currencyCodeSet)
        .map((currencyCode) => {
          const exchangeRate =
            currencyCode === baseCurrencyCode
              ? 1
              : this.resolveCurrencyExchangeRate(
                  currencyRateGraph,
                  baseCurrencyCode,
                  currencyCode
                );

          if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
            return null;
          }

          const matchedCurrencyItem =
            normalizedCurrencyList.find((item) => {
              return (
                item.fromCurrencyCode === baseCurrencyCode &&
                item.toCurrencyCode === currencyCode
              );
            }) ||
            normalizedCurrencyList.find((item) => {
              return item.toCurrencyCode === currencyCode;
            }) ||
            normalizedCurrencyList.find((item) => {
              return item.fromCurrencyCode === currencyCode;
            }) ||
            {};

          return {
            ...matchedCurrencyItem,
            id: matchedCurrencyItem.id || `${baseCurrencyCode}_${currencyCode}`,
            fromCurrencyCode: baseCurrencyCode,
            toCurrencyCode: currencyCode,
            exchangeRate,
            activeFlag: get(matchedCurrencyItem, "activeFlag", true),
            deleteFlag: get(matchedCurrencyItem, "deleteFlag", false),
          };
        })
        .filter(Boolean);

      return this.buildCurrencyList(displayCurrencyList, baseCurrencyCode);
    },
    async fetchShopDefaultCurrency(serverShopId, $fetch, headers) {
      if (!serverShopId) {
        traceSsr("currency.fetchShopDefaultCurrency.skip", {
          reason: "missing_server_shop_id",
        });
        return "";
      }

      traceSsr("currency.fetchShopDefaultCurrency.request", {
        serverShopId,
      });

      try {
        const response = await queryDefaultCurrencyCode(
          {
            param: {
              dealerShopId:serverShopId,
            },
          },
          $fetch,
          headers
        );

        const defaultCurrencyCode = [
          get(response, "data.data.defaultCurrencyCode"),
          get(response, "data.defaultCurrencyCode"),
          get(response, "defaultCurrencyCode"),
          typeof get(response, "data.data") === "string"
            ? get(response, "data.data")
            : "",
          typeof get(response, "data") === "string" ? get(response, "data") : "",
        ].find((item) => {
          return String(item || "").trim();
        });

        const normalizedDefaultCurrencyCode = String(defaultCurrencyCode || "")
          .trim()
          .toUpperCase();

        if (normalizedDefaultCurrencyCode) {
          this.setShopDefaultCurrencyCode(
            serverShopId,
            normalizedDefaultCurrencyCode
          );
        }

        traceSsr("currency.fetchShopDefaultCurrency.response", {
          serverShopId,
          success: response?.success,
          status: response?.status,
          msg: response?.msg,
          normalizedDefaultCurrencyCode,
        });

        return normalizedDefaultCurrencyCode;
      } catch (error) {
        traceSsr("currency.fetchShopDefaultCurrency.error", {
          serverShopId,
          message: error?.message || String(error || ""),
        });
        console.log("fetchShopDefaultCurrency error", error);
        return "";
      }
    },
    async fetchCurrencyRateByPair(
      dealerShopId,
      fromCurrencyCode,
      toCurrencyCode,
      $fetch,
      headers
    ) {
      if (!dealerShopId || !fromCurrencyCode || !toCurrencyCode) {
        traceSsr("currency.fetchCurrencyRateByPair.skip", {
          dealerShopId,
          fromCurrencyCode,
          toCurrencyCode,
          reason: "missing_required_param",
        });
        return null;
      }

      traceSsr("currency.fetchCurrencyRateByPair.request", {
        dealerShopId,
        fromCurrencyCode,
        toCurrencyCode,
      });

      try {
        const response = await queryCurrencyRate(
          {
            param: {
              dealerShopId,
              fromCurrencyCode,
              toCurrencyCode,
            },
          },
          $fetch,
          headers
        );

        const rawRateData =
          get(response, "data.data") ||
          get(response, "data") ||
          get(response, "data.dataList[0]") ||
          get(response, "data.currencyRateDTO") ||
          {};

        const normalizedExchangeRate = Number(
          get(rawRateData, "exchangeRate", rawRateData)
        );

        if (!normalizedExchangeRate || Number.isNaN(normalizedExchangeRate)) {
          traceSsr("currency.fetchCurrencyRateByPair.empty", {
            dealerShopId,
            fromCurrencyCode,
            toCurrencyCode,
            success: response?.success,
            status: response?.status,
            msg: response?.msg,
          });
          return null;
        }

        const rateItem = {
          id: get(rawRateData, "id", `${fromCurrencyCode}_${toCurrencyCode}`),
          fromCurrencyCode,
          toCurrencyCode,
          exchangeRate: normalizedExchangeRate,
          activeFlag: true,
          deleteFlag: false,
          ...rawRateData,
        };

        traceSsr("currency.fetchCurrencyRateByPair.response", {
          dealerShopId,
          fromCurrencyCode,
          toCurrencyCode,
          exchangeRate: normalizedExchangeRate,
        });

        return rateItem;
      } catch (error) {
        traceSsr("currency.fetchCurrencyRateByPair.error", {
          dealerShopId,
          fromCurrencyCode,
          toCurrencyCode,
          message: error?.message || String(error || ""),
        });
        console.log("fetchCurrencyRateByPair error", error);
        return null;
      }
    },
    resolveCurrentCurrency(currencyList = [], defaultCurrencyCode = "") {
      const normalizedDefaultCurrencyCode = String(defaultCurrencyCode || "")
        .trim()
        .toUpperCase();
      const normalizedCurrentCurrencyCode = String(
        get(this.currentCurrency, "toCurrencyCode") || ""
      )
        .trim()
        .toUpperCase();

      const matchedCurrentCurrency = currencyList.find((item) => {
        return (
          String(item?.toCurrencyCode || "").trim().toUpperCase() ===
          normalizedCurrentCurrencyCode
        );
      });
      if (matchedCurrentCurrency) {
        return matchedCurrentCurrency;
      }

      const matchedDefaultCurrency = currencyList.find((item) => {
        return (
          String(item?.toCurrencyCode || "").trim().toUpperCase() ===
          normalizedDefaultCurrencyCode
        );
      });
      if (matchedDefaultCurrency) {
        return matchedDefaultCurrency;
      }

      return currencyList[0] || {};
    },

    // 深度设置菜单 ID Map
    deepSetIdMap(menuData, typeMap) {
      menuData.forEach((o) => {
        if (o.id) {
          typeMap[`id_${o.id}`] = o;
        }

        if (o.noticeColumnEntityList && o.noticeColumnEntityList.length) {
          this.deepSetIdMap(o.noticeColumnEntityList, typeMap);
        }
      });
    },

    // 获取用户信息
    async fetchUserInfo() {},

    // 获取 Token
    getTokenByKey(key) {
      return this.tokenKeyMap[key] || mockJwtMap[getPrefixUrl()];
    },
    // 获取站点菜单
    async fetchSiteMenu(currentServer = getCurrentServer()) {
      const dealerShopId =
        this.getDealerShopId(currentServer) || this.getServerShopId(currentServer);
      const shopId = this.getShopId(currentServer);
      const param = {
        pageNo: 0,
        pageSize: 9999,
        enabled: 1,
      };

      if (dealerShopId) {
        param.dealerShopId = dealerShopId;
      }

      if (shopId) {
        param.shopId = shopId;
      }

      traceSsr("fetchSiteMenu.request", {
        currentServer,
        dealerShopId,
        shopId,
      });

      try {
        const res = await withDevelopmentTimeout(
          getMallGoodsMenuList({ param })
        );
        const dataList = Array.isArray(res.data) ? res.data : res.data?.dataList;
        if (dataList && dataList.length) {
          const list = normalizeMallGoodsMenuList(dataList);
          this.navigationList = toPlainSerializable(list);
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "Error fetching site menu, continuing with empty navigation:",
            error?.message || error
          );
          this.navigationList = [];
        } else {
          console.error("Error fetching site menu:", error);
        }
      }
      return this.navigationList;
    },

    // 获取网站店铺信息
    async fetchStoreInfo(currentServer) {
      let storeRes = {};

      traceSsr("fetchStoreInfo.request", {
        currentServer,
      });

      try {
        storeRes = await getWebStorePageInfo({ currentServer });
      } catch (e) {
        console.error("Error fetching store info:", e);
      }

      traceSsr("fetchStoreInfo.response", {
        currentServer,
        success: storeRes?.success,
        status: storeRes?.status,
        msg: storeRes?.msg,
        storeInfoId: get(storeRes, "data.id"),
        storeInfoTitle: get(storeRes, "data.title"),
        storeInfoShopStyle: get(storeRes, "data.shopStyle"),
        storeInfoStoreStyle: get(storeRes, "data.storeStyle"),
        storeInfoThemeStyle: get(storeRes, "data.themeStyle"),
      });

      if (storeRes?.data) {
        try {
          if (typeof get(storeRes, "data.offiaccountFollowUrl") == "string") {
            set(
              storeRes,
              "data.storePageInfo",
              JSON.parse(get(storeRes, "data.offiaccountFollowUrl"))
            );
          }
        } catch (e) {
          console.log(e);
        }
        try {
          if (
            typeof get(storeRes, "data.storePageInfo.otherJson") == "string"
          ) {
            set(
              storeRes,
              "data.storePageInfo",
              JSON.parse(get(storeRes, "data.storePageInfo.otherJson"))
            );
          }
        } catch (e) {
          console.log(e);
        }

        this.setStoreInfoDataMap(currentServer, storeRes.data);
        traceSsr("fetchStoreInfo.stored", {
          currentServer,
          storedStoreInfoId: get(this.storeInfoDataMap, `${currentServer}.id`),
          storedStoreInfoTitle: get(this.storeInfoDataMap, `${currentServer}.title`),
          storedShopStyle: get(this.storeInfoDataMap, `${currentServer}.shopStyle`),
          storedStoreStyle: get(this.storeInfoDataMap, `${currentServer}.storeStyle`),
          storedThemeStyle: get(this.storeInfoDataMap, `${currentServer}.themeStyle`),
          storedMallSiteConfigDealerShopId: get(this.storeInfoDataMap, `${currentServer}.storePageInfo.mallSiteConfig.dealerShopId`) || "",
        });
      }
    },

    // 获取站点信息
    async fetchSiteInfo(currentServer) {
      let siteKeyRes = {};

      traceSsr("fetchSiteInfo.request", { currentServer });

      if (shouldUseDevelopmentSiteInfoFallback()) {
        siteKeyRes = {
          success: true,
          status: "SUCCESS",
          msg: "development fallback",
          data: createDevelopmentFallbackSiteInfo(currentServer),
        };
      } else {
        try {
          siteKeyRes = await withDevelopmentTimeout(
            getWebsiteHomePageInfo({ currentServer })
          );
        } catch (e) {
          if (process.env.NODE_ENV === "development") {
            console.warn(
              "Error fetching site info, using development fallback:",
              e?.message || e
            );
            siteKeyRes = {
              success: true,
              status: "SUCCESS",
              msg: "development fallback",
              data: createDevelopmentFallbackSiteInfo(currentServer),
            };
          } else {
            console.error("Error fetching site info:", e);
          }
        }
      }

      if (!siteKeyRes?.data && process.env.NODE_ENV === "development") {
        console.warn(
          "Empty site info response, using development fallback"
        );
        siteKeyRes = {
          success: true,
          status: "SUCCESS",
          msg: "development fallback",
          data: createDevelopmentFallbackSiteInfo(currentServer),
        };
      }

      traceSsr("fetchSiteInfo.response", {
        currentServer,
        success: siteKeyRes?.success,
        status: siteKeyRes?.status,
        msg: siteKeyRes?.msg,
        siteInfoId: get(siteKeyRes, "data.id"),
        siteInfoTitle: get(siteKeyRes, "data.title"),
        siteInfoTemplateKey: get(siteKeyRes, "data.templateKey"),
        siteInfoConfigDomainTree: get(siteKeyRes, "data.configData.domainTree") || "",
      });

      if (siteKeyRes?.data) {
        this.setSiteInfo(siteKeyRes.data);
        
        // 保存 domainName（从 domainTree 数组中获取主域名）
        const configData = getSiteConfigData(siteKeyRes.data);
        let domainTree = configData?.domainTree || [];
        
        // domainTree 是数组，取第一个主域名
        if (domainTree && domainTree.length > 0 && domainTree[0]?.domain) {
          this.setDomainName(domainTree[0].domain);
          console.log('已保存 domainName:', domainTree[0].domain);
        }

        traceSsr("fetchSiteInfo.stored", {
          currentServer,
          storedSiteInfoId: get(this.siteInfo, "id"),
          storedSiteInfoTitle: get(this.siteInfo, "title"),
          storedDomainName: this.domainName,
          domainTreeLength: Array.isArray(domainTree) ? domainTree.length : 0,
        });
      }
    },

    async fetchLayoutConfig(currentServer = getCurrentServer()) {
      let layoutData = {};
      if (
        this.siteInfo?.id === "development-site-fallback"
      ) {
        this.layoutData = layoutData;
        return;
      }
      const storeInfo = this.getStoreInfoDataMap(currentServer) || {};
      const storePageInfo = get(storeInfo, "storePageInfo", {}) || {};
      const siteTemplate = resolveSiteTemplate(this.getSiteInfo(), {
        ...storeInfo,
        ...storePageInfo,
      });
      const resources = siteTemplate.resources || {};
      const commonLayouts = [
        resources.layoutHeaderUrl,
        resources.layoutFooterUrl,
        resources.userHeaderUrl,
        resources.userFooterUrl,
      ].filter(Boolean);
      const uniqueLayouts = [...new Set(commonLayouts)];
      try {
        for (let i = 0; i < uniqueLayouts.length; i++) {
          let layoutConfigItemResponse = await getActiveDetail({
            url: uniqueLayouts[i],
            pageNo: 0,
            pageSize: 1,
          });
          if (get(layoutConfigItemResponse, "data.dataList[0]")) {
            let areaData = get(
              layoutConfigItemResponse,
              "data.dataList[0].areaData"
            );
            let areaDataParseData = null;
            try {
              areaDataParseData = JSON.parse(areaData);
            } catch (e) {
              console.log(e);
            }
            if (areaDataParseData) {
              layoutData[uniqueLayouts[i]] = areaDataParseData;
            }
          }
        }

  
      } catch (e) {
        console.error("Error fetching site info:", e);
      }
      this.layoutData = toPlainSerializable(layoutData);
    },
    async getCurrentUserData() {
      let appToken = window.localStorage.getItem(config.appTokenKey);
      let mallAppToken = window.localStorage.getItem(config.mallAppTokenKey);
      let currentUserData = {};
      let currentMallUserData = {};
      if (appToken) {
        let currentUserDataRes = await getCurrentUserData({});

        const responseCartList = await getCartList();
        console.log("responseCartList",responseCartList)

        currentUserData = get(currentUserDataRes, "data.dataList[0]", {});

        currentUserData.cartNum=get(responseCartList, "data.dataList.length", 0);
      }else{


        const localCartData = window.localStorage.getItem("local_cart_items")
        if (localCartData) {
          try{
            let  cartList = JSON.parse(localCartData)
            currentUserData.cartNum=cartList.length;
          }catch(e){

          }
        } 
      }

      // if (mallAppToken && currentUserData.memberCode) {
      //   let currentMallUserDataRes = await getCurrentMallUserData({
      //     param: {
      //       thirdParty: 5,
      //       thirdPartyType: "SZT",
      //       thirdPartyId: currentUserData.memberCode,
      //     },
      //   });

      //   currentMallUserData = get(currentMallUserDataRes, "data", {});
      // }

      this.userInfo = toPlainSerializable(currentUserData);
      this.mallUserInfo = toPlainSerializable(currentUserData);
    },
    saveCurrentCurrency(toCurrency, currentServer = getCurrentServer()) {
      try {
        if (toCurrency) {
          window.localStorage.setItem(
            getCurrentCurrencyStorageKey(currentServer),
            JSON.stringify(toCurrency)
          );

          this.currentCurrency = toPlainSerializable(toCurrency);
        }
      } catch (e) {
        console.log(e);
      }
    },
    initCurrentCurrency(currentServer = getCurrentServer()) {
      try {
        let currentCurrency = readStoredCurrentCurrency(currentServer);
        if (currentCurrency && typeof currentCurrency === "object") {
          this.currentCurrency = toPlainSerializable(currentCurrency);
          return;
        }
        this.currentCurrency = {};
      } catch (e) {
        console.log(e);
      }
    },
    async getCurrencyData(options = {}) {
      const {
        currentServer = getCurrentServer(),
        $fetch,
        headers,
        forceRefresh = false,
      } = options;

      if (process.client) {
        this.initCurrentCurrency(currentServer);
      }

      const fallbackDefaultCurrencyCode =
        this.getMallSiteDefaultCurrency(currentServer) || "TWD";
      traceSsr("currency.getCurrencyData.start", {
        currentServer,
        forceRefresh,
        fallbackDefaultCurrencyCode,
        serverShopId: this.getServerShopId(currentServer),
        cachedCurrentCurrencyCode: get(this.currentCurrency, "toCurrencyCode") || "",
      });
      const applyCurrencyState = (
        rawCurrencyList = [],
        defaultCurrencyCode = fallbackDefaultCurrencyCode
      ) => {
        const nextCurrencyList = this.buildCurrencyList(
          Array.isArray(rawCurrencyList) ? rawCurrencyList : [],
          defaultCurrencyCode
        );
        const nextCurrentCurrency = this.resolveCurrentCurrency(
          nextCurrencyList,
          defaultCurrencyCode
        );

        this.currencyList = toPlainSerializable(nextCurrencyList);

        if (get(nextCurrentCurrency, "toCurrencyCode")) {
          if (process.client) {
            this.saveCurrentCurrency(nextCurrentCurrency, currentServer);
          } else {
            this.currentCurrency = toPlainSerializable(nextCurrentCurrency);
          }
        }

        traceSsr("currency.getCurrencyData.applied", {
          currentServer,
          defaultCurrencyCode,
          currencyListLength: Array.isArray(nextCurrencyList) ? nextCurrencyList.length : 0,
          currentCurrencyCode: get(nextCurrentCurrency, "toCurrencyCode") || "",
          currentExchangeRate: get(nextCurrentCurrency, "exchangeRate") || "",
        });

        return this.currencyList;
      };

      const serverShopId = this.getServerShopId(currentServer);
      if (!serverShopId) {
        traceSsr("currency.getCurrencyData.noShopId", {
          currentServer,
          fallbackDefaultCurrencyCode,
        });
        return applyCurrencyState([], fallbackDefaultCurrencyCode);
      }

      const cachedDefaultCurrencyCode =
        this.getShopDefaultCurrencyCode(serverShopId);

      if (
        !forceRefresh &&
        Array.isArray(this.currencyList) &&
        this.currencyList.length &&
        cachedDefaultCurrencyCode
      ) {
        traceSsr("currency.getCurrencyData.useCache", {
          currentServer,
          serverShopId,
          cachedDefaultCurrencyCode,
          currencyListLength: this.currencyList.length,
        });
        return this.currencyList;
      }

      let defaultCurrencyCode =
        cachedDefaultCurrencyCode || fallbackDefaultCurrencyCode;
      try {
        defaultCurrencyCode =
          (await this.fetchShopDefaultCurrency(serverShopId, $fetch, headers)) ||
          fallbackDefaultCurrencyCode;
      } catch (error) {
        console.log("fetch default currency before currency list error", error);
      }

      let currencyList = [];
      try {
        const currentUserDataRes = await getCurrencyDataList(
          {
            param: {
              dealerShopId: serverShopId,
            },
          },
          $fetch,
          headers
        );

        currencyList =
          get(currentUserDataRes, "data.currencyRateDTOList") ??
          get(currentUserDataRes, "data.dataList") ??
          get(currentUserDataRes, "data") ??
          [];

        traceSsr("currency.getCurrencyData.currencyList", {
          currentServer,
          serverShopId,
          defaultCurrencyCode,
          currencyListLength: Array.isArray(currencyList) ? currencyList.length : 0,
          success: currentUserDataRes?.success,
          status: currentUserDataRes?.status,
          msg: currentUserDataRes?.msg,
        });
      } catch (error) {
        traceSsr("currency.getCurrencyData.currencyListError", {
          currentServer,
          serverShopId,
          message: error?.message || String(error || ""),
        });
        console.log("getCurrencyData error", error);
      }

      try {
        let nextCurrencyList = this.buildDisplayCurrencyList(
          currencyList,
          defaultCurrencyCode
        );

        const hasDefaultCurrency = nextCurrencyList.some((item) => {
          return (
            String(item?.toCurrencyCode || "").trim().toUpperCase() ===
            defaultCurrencyCode
          );
        });

        if (
          !hasDefaultCurrency &&
          defaultCurrencyCode !== fallbackDefaultCurrencyCode
        ) {
          const defaultCurrencyRateItem = await this.fetchCurrencyRateByPair(
            serverShopId,
            fallbackDefaultCurrencyCode,
            defaultCurrencyCode,
            $fetch,
            headers
          );
          if (defaultCurrencyRateItem) {
            nextCurrencyList = this.buildDisplayCurrencyList([
              ...nextCurrencyList,
              defaultCurrencyRateItem,
            ], defaultCurrencyCode);
          }
        }

        return applyCurrencyState(nextCurrencyList, defaultCurrencyCode);
      } catch (error) {
        traceSsr("currency.getCurrencyData.applyError", {
          currentServer,
          serverShopId,
          defaultCurrencyCode,
          fallbackDefaultCurrencyCode,
          message: error?.message || String(error || ""),
        });
        console.log("apply currency data error", error);
        return applyCurrencyState(
          this.buildDisplayCurrencyList(currencyList, fallbackDefaultCurrencyCode),
          fallbackDefaultCurrencyCode
        );
      }
    },
    async initCurrencyData(options = {}) {
      traceSsr("currency.initCurrencyData", {
        currentServer: options?.currentServer || getCurrentServer(),
        forceRefresh: !!options?.forceRefresh,
      });
      return this.getCurrencyData(options);
    },

    async logout() {
      this.userInfo = {};
      this.mallUserInfo = {};
      removeToken();
      removeMallAppToken();
      if (process.client) {
        window.location.href="/sub/login";
      }
    },
  },
});
